import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { SaveIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import {
  getLastSearchedVehicleKey,
  getLastVehicleDetail,
  setLastSearchedVehicleKey,
} from 'domains/catalog/Catalog.store';
import { useFetchAvailableDMSServices } from 'domains/dms/DMS.requests';
import { useFetchEstimate, useFetchEstimateSettingsSubscription } from 'domains/estimate/Estimate.requests';
import {
  createEstimateFromDMSRequest,
  createNewEstimate,
  getCurrentEstimateId,
  getEstimateById,
  getEstimateSequenceNumber,
} from 'domains/estimate/Estimate.store';
import { EstimateIdParam } from 'domains/estimate/Estimate.types';
import { getAvailableDMSService } from 'domains/user';
import EstimateModifyTab from 'pages/EstimatePage/EstimateModifyTab';
import { hydrateCatalogFromEstimateVehicle } from 'pages/EstimatePage/hydrateCatalogFromEstimateVehicle';
import { Box, CenterFlex, Container, defaultPadding, Flex, Text, WhiteButton } from 'UI';
import { getData } from 'utils';
import {
  TRACKING_EVENT_CREATE_NEW_ESTIMATE,
  TRACKING_EVENT_CREATE_NEW_ESTIMATE_CONFIRM,
} from 'utils/eventTracker/EventTracker.types';
import { useResetScroll } from 'utils/hooks/useResetScroll';
import CreateEstimateButton from './CreateEstimateButton';
import { SPopover } from './EstimatePage.styled';

export default function EstimatePage({ estimateId }: Readonly<{ estimateId: string }>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const vehicle = useSelector(getLastVehicleDetail) as VehicleDetail;
  const disclosure = useDisclosure();
  const [isCreateEstimatePopoverVisible, setIsCreateEstimatePopoverVisible] = useState(false);
  const [isDMSImportActive, setIsDMSImportActive] = useState(false);

  const { onOpen } = disclosure;
  const history = useHistory();
  const sequenceNumber = useSelector((state: RootState) => getEstimateSequenceNumber(state, estimateId));
  const isDMSAvailable = useSelector((state: RootState) => getAvailableDMSService(state, 'ESTIMATE'));
  const currentEstimateId = useSelector(getCurrentEstimateId);
  const params = useMemo(
    () => new URLSearchParams(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEstimateId, location.search],
  );
  const estimate = useSelector((state: RootState) => getEstimateById(state, estimateId));
  const estimateData = getData(estimate);
  const catalogVehicleKey = useSelector(getLastSearchedVehicleKey);
  const estimateVehicle = estimateData?.vehicle;

  useResetScroll([estimateId]);
  useFetchEstimate(estimateId);
  useFetchAvailableDMSServices();
  useFetchEstimateSettingsSubscription();

  useEffect(() => {
    if (estimateVehicle && estimateVehicle.vehicleKey !== catalogVehicleKey) {
      hydrateCatalogFromEstimateVehicle(dispatch, estimateVehicle);
    }
    if (!estimateVehicle) {
      dispatch(setLastSearchedVehicleKey(undefined));
    }
    // eslint-disable-next-line
  }, [estimateVehicle]);

  const handleConfirm = () => {
    trackAppEvent(TRACKING_EVENT_CREATE_NEW_ESTIMATE_CONFIRM);
    dispatch(createNewEstimate({ vehicle }));
    params.delete(EstimateIdParam);
    history.push(`${ROUTER_ESTIMATE}?${params}`);
  };

  function createEstimate() {
    trackAppEvent(TRACKING_EVENT_CREATE_NEW_ESTIMATE);
    onOpen();
  }

  function importDMSOrder(orderNumber: string) {
    dispatch(createEstimateFromDMSRequest({ orderNumber }));
  }

  return (
    <Container data-cy={'estimate-page'} padding={defaultPadding}>
      <CenterFlex>
        <Dialog
          disclosure={disclosure}
          title={t('common.dialog.actionRequired', 'Action required')}
          description={t(
            'estimate.create_new.description',
            'You’re about to create a new estimate, your current one will be saved in your estimate  history.',
          )}
          icon={SaveIcon}
          status={'info'}
          handleConfirm={handleConfirm}
        />
      </CenterFlex>
      <Flex direction={'row'} align={'center'} justify={'space-between'} padding={'0px 0px 10px 0px'}>
        <Text type={'h1'} disableGutter data-cy={'estimate-title'}>
          {t('estimate', 'Estimate')}
          {sequenceNumber && ` ${sequenceNumber}`}
        </Text>
        <Box data-cy={'create-new-estimate-button'}>
          <WhiteButton
            size={'middle'}
            onClick={() => {
              if (!isDMSAvailable) {
                createEstimate();
                return;
              }
              setIsCreateEstimatePopoverVisible(true);
            }}
          >
            {t('estimate.create_new', 'Create new estimate')}
          </WhiteButton>
          <SPopover
            id={'create-new-estimate-popover'}
            getPopupContainer={(trigger) => trigger.parentElement ?? trigger}
            content={
              <CreateEstimateButton
                createEstimate={createEstimate}
                isDMSAvailable={isDMSAvailable}
                importDMSOrder={importDMSOrder}
                setIsCreateEstimatePopoverVisible={setIsCreateEstimatePopoverVisible}
                isDMSImportActive={isDMSImportActive}
                setIsDMSImportActive={setIsDMSImportActive}
              />
            }
            trigger={'click'}
            placement={'bottomRight'}
            visible={isCreateEstimatePopoverVisible}
            onVisibleChange={(visible) => {
              setIsCreateEstimatePopoverVisible(visible);
              if (visible === false) {
                setIsDMSImportActive(false);
              }
            }}
          />
        </Box>
      </Flex>
      <EstimateModifyTab estimateId={currentEstimateId} key={currentEstimateId} />
    </Container>
  );
}
